import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'types/Store';
import { invalidateBillingCaches } from 'app/modules/settings/modules/billing/utils/billingCaches';

/**
 * Re-read the user's permission list into Redux.
 *
 * Permissions are otherwise loaded once at app init, and RouteAccessGuard
 * enforces from that list. Activating a module (e.g. Analytics) grants its
 * permission server-side, so without this re-read the guard keeps denying the
 * module's pages until a full reload. The thunk logs and swallows a failed
 * request, so this resolves either way.
 *
 * `userActions` is imported lazily on purpose: it sits on the import cycle
 * `userActions` → `api/user` → `api/client` → store → `userReducer` →
 * `userActions`. A static import here means anything that loads this module
 * before the store (e.g. tests of `useNewBillingRoutesAvailable`) enters the
 * cycle at `userActions` and builds the store while `UserActionNames` is still
 * undefined. By the time a billing mutation succeeds the store is long built,
 * so the deferred import is safe.
 */
export const refreshUserPermissions = async (
  dispatch: Dispatch,
): Promise<void> => {
  const { UserThunks } = await import('modules/user/userActions');
  await dispatch(UserThunks.loadUserPermissions());
};

/**
 * {@link invalidateBillingCaches} plus {@link refreshUserPermissions}, for the
 * mutations that can change what the account is entitled to: trial checkout,
 * plan change, module activate / cancel, and subscription reactivate. Fire-and-forget: the
 * mutation settles on the billing response, not on the permission re-read.
 */
export const useRefreshBillingEntitlements = (): (() => void) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();

  return () => {
    invalidateBillingCaches(queryClient);
    void refreshUserPermissions(dispatch);
  };
};
